/**
 * Offline Token Validator
 * Provides client-side JWT token validation for offline scenarios
 * Requirements: 9.6, 9.7
 */

interface JWTPayload {
  userId: string;
  email: string;
  exp: number;
  iat: number;
  [key: string]: any;
}

interface OfflineTokenValidationResult {
  isValid: boolean;
  isExpired: boolean;
  payload: JWTPayload | null;
  error?: string;
}

/**
 * Decode JWT token without verification (for offline use only)
 * Note: This is NOT secure for server-side validation, only for offline client-side checks
 */
function decodeJWTPayload(token: string): JWTPayload | null {
  try {
    // JWT structure: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (base64url)
    const payload = parts[1];
    // Add padding if needed for base64 decoding
    const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
    const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
    
    return JSON.parse(decodedPayload) as JWTPayload;
  } catch (error) {
    console.error('Failed to decode JWT payload:', error);
    return null;
  }
}

/**
 * Validate JWT token structure and expiration (offline validation only)
 */
export function validateTokenOffline(token: string | null): OfflineTokenValidationResult {
  if (!token) {
    return {
      isValid: false,
      isExpired: false,
      payload: null,
      error: 'No token provided'
    };
  }

  // Check basic JWT structure
  if (typeof token !== 'string' || token.split('.').length !== 3) {
    return {
      isValid: false,
      isExpired: false,
      payload: null,
      error: 'Invalid token structure'
    };
  }

  // Decode payload
  const payload = decodeJWTPayload(token);
  if (!payload) {
    return {
      isValid: false,
      isExpired: false,
      payload: null,
      error: 'Failed to decode token payload'
    };
  }

  // Check required fields
  if (!payload.userId || !payload.email || !payload.exp || !payload.iat) {
    return {
      isValid: false,
      isExpired: false,
      payload,
      error: 'Token missing required fields'
    };
  }

  // Check expiration
  const now = Math.floor(Date.now() / 1000); // Current time in seconds
  const isExpired = payload.exp <= now;

  return {
    isValid: !isExpired,
    isExpired,
    payload,
    error: isExpired ? 'Token has expired' : undefined
  };
}

/**
 * Check if token will expire within a given time frame (in seconds)
 */
export function isTokenExpiringWithin(token: string | null, seconds: number): boolean {
  const validation = validateTokenOffline(token);
  if (!validation.payload) {
    return true; // Consider invalid tokens as "expiring"
  }

  const now = Math.floor(Date.now() / 1000);
  const expiresIn = validation.payload.exp - now;
  
  return expiresIn <= seconds;
}

/**
 * Get token expiration time as Date object
 */
export function getTokenExpirationDate(token: string | null): Date | null {
  const validation = validateTokenOffline(token);
  if (!validation.payload) {
    return null;
  }

  return new Date(validation.payload.exp * 1000);
}

/**
 * Get token issued time as Date object
 */
export function getTokenIssuedDate(token: string | null): Date | null {
  const validation = validateTokenOffline(token);
  if (!validation.payload) {
    return null;
  }

  return new Date(validation.payload.iat * 1000);
}

/**
 * Get user ID from token (offline)
 */
export function getUserIdFromToken(token: string | null): string | null {
  const validation = validateTokenOffline(token);
  return validation.payload?.userId || null;
}

/**
 * Get user email from token (offline)
 */
export function getUserEmailFromToken(token: string | null): string | null {
  const validation = validateTokenOffline(token);
  return validation.payload?.email || null;
}

/**
 * Check if token belongs to a specific user
 */
export function isTokenForUser(token: string | null, userId: string): boolean {
  const tokenUserId = getUserIdFromToken(token);
  return tokenUserId === userId;
}

/**
 * Get time remaining until token expires (in seconds)
 */
export function getTokenTimeRemaining(token: string | null): number {
  const validation = validateTokenOffline(token);
  if (!validation.payload) {
    return 0;
  }

  const now = Math.floor(Date.now() / 1000);
  const remaining = validation.payload.exp - now;
  
  return Math.max(0, remaining);
}

/**
 * Format token expiration for display
 */
export function formatTokenExpiration(token: string | null): string {
  const expirationDate = getTokenExpirationDate(token);
  if (!expirationDate) {
    return 'Invalid token';
  }

  const now = new Date();
  if (expirationDate <= now) {
    return 'Expired';
  }

  const timeRemaining = getTokenTimeRemaining(token);
  
  if (timeRemaining < 60) {
    return `Expires in ${timeRemaining} seconds`;
  } else if (timeRemaining < 3600) {
    const minutes = Math.floor(timeRemaining / 60);
    return `Expires in ${minutes} minute${minutes !== 1 ? 's' : ''}`;
  } else if (timeRemaining < 86400) {
    const hours = Math.floor(timeRemaining / 3600);
    return `Expires in ${hours} hour${hours !== 1 ? 's' : ''}`;
  } else {
    const days = Math.floor(timeRemaining / 86400);
    return `Expires in ${days} day${days !== 1 ? 's' : ''}`;
  }
}

/**
 * Offline token validation summary for debugging
 */
export function getTokenValidationSummary(token: string | null): {
  hasToken: boolean;
  isStructurallyValid: boolean;
  isExpired: boolean;
  userId: string | null;
  email: string | null;
  expiresAt: Date | null;
  timeRemaining: number;
} {
  const validation = validateTokenOffline(token);
  
  return {
    hasToken: !!token,
    isStructurallyValid: !!validation.payload,
    isExpired: validation.isExpired,
    userId: validation.payload?.userId || null,
    email: validation.payload?.email || null,
    expiresAt: validation.payload ? new Date(validation.payload.exp * 1000) : null,
    timeRemaining: getTokenTimeRemaining(token)
  };
}